import { Material, Thumbnail, MaterialFolder } from 'api';
import { motion } from 'framer-motion';
import { UI } from 'helpers/constants';
import { formatMoney } from 'helpers/util';
import Typography from 'helpers/fonts';
import { useSmallScreen } from 'helpers/isSmallScreen';
import { getImageUrl } from 'helpers/selectExistingImage';
import { useHover } from 'helpers/useHover';
import React from 'react';
import './tabpane-squares.css';
import { useAppState } from 'state/appstate/appstate';
import { CEDAR_MATERIAL_ID, MaterialOrientation } from './tabpane';
import { useUiState } from 'state/ui-state/ui-state';

export interface TabPaneSquaresProps {
  options: Array<Material>;
  position: 'siding' | 'trim' | 'roofing';
  orientation: MaterialOrientation;
  changeOrientation: (o: MaterialOrientation) => void;
  materialFolder: MaterialFolder['id'] | undefined;
}

interface TabPaneSquareProps {
  active: boolean;
  material: Material;
  setFunction: () => void;
}

const TabPaneSquare: React.FunctionComponent<TabPaneSquareProps> = ({
  active,
  material,
  setFunction,
}) => {
  const [ref, isHovered] = useHover();
  const isSmallScreen = useSmallScreen();
  // console.log(material);
  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: 'auto',
          width: '100%',
          maxWidth: isSmallScreen ? '100px' : '127px',
          background: Theme['@white'],
          borderRadius: '4px',
          border: isHovered || active ? '1px solid #7a9c49' : '1px solid transparent',
        }}
        ref={ref}
        className={'square'}
        onClick={() => setFunction()}
      >
        <img
          style={{
            maxWidth: '100%',
            width: '100%',
            justifySelf: 'center',
            borderRadius: '4px 4px 0px 0px',
          }}
          src={getImageUrl(material.thumbnail as Thumbnail)}
          alt=""
        />
        <div
          style={{
            margin: '8px 8px',
            overflow: 'hidden',
            lineHeight: 1
          }}
        >
          <Typography
            styleid={9}
            style={{
              color: isHovered || active ? Theme['@primary-color'] : Theme['@black'],
              lineHeight: '12px',
            }}
          >
            {material.title}
          </Typography> <br/>
          <Typography
            styleid={9}
            style={{
              color: isHovered || active ? Theme['@primary-color'] : Theme['@black'],
              lineHeight: '20px'
            }}
          >
            {Number(material.pricePerUnit) <= 0 ? 'Cost Included' : `${formatMoney(Number(material.pricePerUnit))}/${material.priceType}`}
          </Typography>
        </div>
      </div>
    </>
  );
};

const TabPaneSquares: React.FunctionComponent<TabPaneSquaresProps> = ({
  options,
  position,
  orientation,
  changeOrientation,
  materialFolder,
}) => {
  const { selectedColor, setSelectedColor } = useUiState();
  const { setTrim, setRoofing, setSiding, uState } = useAppState();
  const renderParameters: Record<string, (v: string) => void> = {
    siding: setSiding,
    trim: setTrim,
    roofing: setRoofing,
  };

  const setFunction = (material: Material, orientation: MaterialOrientation) => {
    if (position !== 'roofing' && selectedColor[position] !== undefined && materialFolder !== CEDAR_MATERIAL_ID) {
      setSelectedColor({ ...selectedColor, [position]: undefined });
    }
    if (position === 'siding') {
      if (orientation === MaterialOrientation.VERTICAL && !material.verticalSidingId) {
        changeOrientation(MaterialOrientation.HORIZONTAL)
      }
      renderParameters[position](
        orientation === MaterialOrientation.VERTICAL
          ? material.verticalSidingId || material.id
          : material.id);
    } else {
      renderParameters[position](material.id)
    }
  }
  const activeOption = options.find(m => uState!.materials[position] === m.id ||
    uState!.materials[position] === m.verticalSidingId!
  );

  const itemVariants = {
    show: {
      y: 0,
      opacity: 1,
      transition: {
        y: {
          stiffness: 1000,
          velocity: -100,
          ease: UI.EASE_QUINT,
          duration: 0.4,
        },
      },
    },
    hidden: {
      y: 500,
      opacity: 0,
      transition: {
        y: { stiffness: 1000 },
      },
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.07,
      },
    },
  };

  return (
    <>
      {activeOption?.description && (
        <div style={{ margin: '16px 0' }}>
          <Typography styleid={10}>{activeOption?.title}</Typography> - <Typography styleid={9}>{activeOption?.description}</Typography>
        </div>
      )}

      <motion.div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gridGap: '12px',
        }}
        variants={containerVariants}
        initial="hidden"
        animate={'show'}
      >
        {options.map((m, i) => (
          <motion.span variants={itemVariants} key={m.id}>
            <TabPaneSquare
              active={ activeOption?.id === m.id ||
                (position === 'siding' && !!m.verticalSidingId && activeOption?.id === m.verticalSidingId) }
              material={m}
              setFunction={() => setFunction(m, orientation)}
            />
          </motion.span>
        ))}
      </motion.div>
    </>
  );
};

export default TabPaneSquares;
